import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { Box } from '@mui/system';
import { ELEVATION } from '@openx/components-core/lib/theme/elevation';
import { palette } from '@openx/components-core/lib/theme/paletteV2';
import type { DirectDealsListItem } from '@openx/types/directDeals';
import { getFormattedAmountValue } from '@openx/utils/lib/getFormattedAmountValue';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { PublisherLogo } from './PublisherLogo';

const TEXT_ELLIPSIS_STYLES = {
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  display: 'block',
  maxWidth: '100%',
  fontWeight: 'fontWeightBold',
};

type DirectDealsPublisherTileProps = {
  item: DirectDealsListItem;
};

export const DirectDealsPublisherTile = ({ item }: DirectDealsPublisherTileProps) => {
  const { t } = useTranslation();

  const uniqueVisitorsValue = item.mln_unique_visitors ? `${item.mln_unique_visitors} M` : '-';

  return (
    <Box
      data-test={'publisher-tile'}
      sx={{
        display: 'flex',
        position: 'relative',
        maxWidth: '100%',
        overflow: 'hidden',
        width: '100%',

        '& > .MuiCard-root': {
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Card
        sx={{
          boxShadow: ELEVATION.elevation0,
          height: '100%',
        }}
      >
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            padding: 0,
          }}
        >
          <Link to={`/direct-deals/${item.id}/details`}>
            <Box
              sx={{
                borderBottom: 1,
                padding: 1,
                paddingLeft: 2,
                paddingRight: 2,
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: palette.background.boxLight,
                aspectRatio: '16 / 9',
                overflow: 'hidden',
                minHeight: '166px',
              }}
            >
              <PublisherLogo name={item.logo} height={166} />
            </Box>
          </Link>

          <Box
            sx={{
              padding: 2,
              paddingBottom: 0,
              maxWidth: '100%',
            }}
          >
            <Link data-test="tile-publisher-name-link" title={item.name} to={`/direct-deals/${item.id}/details`}>
              <Typography
                data-test="tile-publisher-name"
                noWrap
                variant="body1Bold"
                component="h6"
                sx={TEXT_ELLIPSIS_STYLES}
              >
                {item.name}
              </Typography>
            </Link>

            <Typography
              data-test="tile-publisher-headline"
              noWrap
              variant="body2"
              title={item.name}
              sx={{
                color: 'text.secondary',
                ...TEXT_ELLIPSIS_STYLES,
                display: '-webkit-box',
                whiteSpace: 'initial',
                WebkitLineClamp: '2',
                WebkitBoxOrient: 'vertical',
                fontStyle: 'italic',
              }}
            >
              {item.headline}
            </Typography>

            <Grid
              container
              sx={{
                padding: 0,
                marginTop: 1,
              }}
            >
              <Grid size={4}>
                <Box data-test={'tile-impressions'}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {t('Impression Avg')}
                  </Typography>

                  <Typography variant="body1Bold">{item.mln_impressions} M</Typography>
                </Box>
              </Grid>

              <Grid size={4}>
                <Box data-test={'tile-unique-visitors'}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {t('Unique Visitors')}
                  </Typography>

                  <Typography variant="body1Bold">{uniqueVisitorsValue}</Typography>
                </Box>
              </Grid>

              <Grid size={4}>
                <Box data-test={'tile-cpm-range'}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {t('Suggested CPM')}
                  </Typography>

                  <Typography variant="body1Bold">
                    ${getFormattedAmountValue(item.cpm_low)} - ${getFormattedAmountValue(item.cpm_high)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Grid
              data-test={'tile-geo-region'}
              container
              sx={{
                marginTop: 1,
                padding: 0,
                alignItems: 'baseline',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  marginRight: 1,
                }}
              >
                {t('Geo Region:')}
              </Typography>

              <Typography variant="body1">{item.geo_region.join(', ')}</Typography>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
